import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ProductKey, ProductNavEntry } from "@merge-api/merge-javascript-shared";
import { fetchWithAuth } from "../../api-client/APIClient";
import { showErrorToast } from "../shared/Toasts";
import useAppContext from "../context/useAppContext";
import { isNavigableUrl } from "../shared/utils/isNavigableUrl";

interface CrossProductTokenResponse {
  token: string;
  redirect_url: string;
}

export interface ProductSwitcherState {
  enabled: boolean;
  currentProduct: ProductKey | null;
  products: ProductNavEntry[];
  /** The pane is showing in place of the product's own nav items. */
  isPaneOpen: boolean;
  openPane: () => void;
  closePane: () => void;
  /**
   * The product a switch is in flight for. Cleared when the request fails or
   * the page comes back from the back-forward cache after leaving.
   */
  switchingTo: ProductKey | null;
  switchTo: (product: ProductKey) => void;
}

const SWITCH_FAILED_MESSAGE = "Couldn't switch products. Please try again.";

/**
 * Left-nav product switcher state, read from the current user. The backend
 * decides whether the switcher is on and which products are reachable.
 */
const useProductSwitcher = (): ProductSwitcherState => {
  const { user } = useAppContext();
  const [isPaneOpen, setIsPaneOpen] = useState(false);
  const [switchingTo, setSwitchingTo] = useState<ProductKey | null>(null);
  // A ref as well as state: two clicks in one tick would both read the state as null.
  const inFlight = useRef<ProductKey | null>(null);
  const state = user?.product_switcher;
  const products = useMemo(() => state?.products ?? [], [state?.products]);
  const currentProduct = products.find((entry) => entry.status === "current")?.product ?? null;

  const finish = useCallback(() => {
    inFlight.current = null;
    setSwitchingTo(null);
  }, []);

  const openPane = useCallback(() => setIsPaneOpen(true), []);
  const closePane = useCallback(() => setIsPaneOpen(false), []);

  // The back button restores this page from the back-forward cache as it was left, mid-switch.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      finish();
      setIsPaneOpen(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [finish]);

  const switchTo = useCallback(
    (product: ProductKey) => {
      if (inFlight.current !== null) return;
      if (products.find((entry) => entry.product === product)?.status !== "available") return;

      inFlight.current = product;
      setSwitchingTo(product);

      fetchWithAuth({
        path: "account-bridge/cross-product-token/",
        method: "POST",
        body: { target_product: product },
        onResponse: ({ redirect_url }: CrossProductTokenResponse) => {
          if (!isNavigableUrl(redirect_url)) {
            finish();
            showErrorToast(SWITCH_FAILED_MESSAGE);
            return;
          }
          // Full page load, not a route change: the target product's API sets its
          // own session cookie before redirecting on to its dashboard.
          window.location.assign(redirect_url);
        },
        onError: () => {
          finish();
          showErrorToast(SWITCH_FAILED_MESSAGE);
        },
      });
    },
    [finish, products],
  );

  const enabled = (state?.enabled ?? false) && products.length > 1;

  return {
    enabled,
    currentProduct,
    products,
    isPaneOpen,
    openPane,
    closePane,
    switchingTo,
    switchTo,
  };
};

export default useProductSwitcher;
